/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo } from "react";

// react-router components
import { useLocation, Link } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";
import Avatar from "@mui/material/Avatar";
import Badge from "@mui/material/Badge";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";

// Material Dashboard 2 React example components
import Breadcrumbs from "examples/Breadcrumbs";
import NotificationItem from "examples/Items/NotificationItem";

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";

// Services
import { getUserData } from "services/authServiceAPI";

function DashboardNavbar({ absolute, light, isMini, contractInfo }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator, darkMode } = controller;
  const [openMenu, setOpenMenu] = useState(false);
  const route = useLocation().pathname.split("/").slice(1);

  // Récupérer les données utilisateur
  const userData = getUserData();

  // Fonction pour extraire les initiales du nom
  const getInitials = (name) => {
    if (!name) return "U";
    const parts = name.split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const userInitials = getInitials(userData?.first_name || userData?.email);

  // Calculer les notifications urgentes
  const urgentNotifications = useMemo(() => {
    if (!contractInfo) return [];

    const calculateDaysRemaining = () => {
      const referenceDate = contractInfo.dateVerif || contractInfo.depart;
      if (!referenceDate) return null;

      const [day, month, year] = referenceDate.split("/");
      const refDate = new Date(year, month - 1, day);
      const nextVerifDate = new Date(refDate);
      nextVerifDate.setDate(nextVerifDate.getDate() + 30);

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      nextVerifDate.setHours(0, 0, 0, 0);

      const diffTime = nextVerifDate - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      return { days: diffDays, nextDate: nextVerifDate };
    };

    const result = calculateDaysRemaining();
    if (!result) return [];

    const { days, nextDate } = result;
    const alerts = [];

    const dateStr = nextDate.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
    });

    if (days < 0) {
      alerts.push({
        icon: <Icon>warning</Icon>,
        title: `RETARD - ${contractInfo.immat}`,
        subtitle: `${Math.abs(days)} jour(s) de retard`,
      });
    } else if (days === 0) {
      alerts.push({
        icon: <Icon>today</Icon>,
        title: `Vérification AUJOURD'HUI`,
        subtitle: contractInfo.immat,
      });
    } else if (days <= 3) {
      alerts.push({
        icon: <Icon>notification_important</Icon>,
        title: `Vérification dans ${days} jour(s)`,
        subtitle: `${contractInfo.immat} - ${dateStr}`,
      });
    }

    return alerts;
  }, [contractInfo]);

  const notificationCount = urgentNotifications.length;

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);
  const handleOpenMenu = (event) => setOpenMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(false);

  // Render the notifications menu
  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2 }}
    >
      {urgentNotifications.length > 0 ? (
        urgentNotifications.map((notif, index) => (
          <NotificationItem
            key={index}
            icon={notif.icon}
            title={notif.title}
            subtitle={notif.subtitle}
          />
        ))
      ) : (
        <NotificationItem
          icon={<Icon>check_circle</Icon>}
          title="Aucune notification urgente"
        />
      )}
    </Menu>
  );

  // Styles for the navbar icons
  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;

      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }

      return colorValue;
    },
  });

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          sx={{ flexWrap: { xs: "nowrap", md: "wrap" } }}
        >
          <MDBox color="inherit" sx={(theme) => navbarRow(theme, { isMini })}>
            <Breadcrumbs icon="home" title={route[route.length - 1]} route={route} light={light} />
          </MDBox>

          {/* Icônes à droite sur la même ligne */}
          <MDBox display="flex" alignItems="center" gap={1}>
            {/* Menu hamburger - visible sur mobile */}
            <IconButton
              size="small"
              disableRipple
              color="inherit"
              sx={{ ...navbarMobileMenu, display: { xs: "inline-flex", lg: "none" } }}
              onClick={handleMiniSidenav}
            >
              <Icon sx={iconsStyle} fontSize="medium">
                {miniSidenav ? "menu_open" : "menu"}
              </Icon>
            </IconButton>

            {/* Notifications - visible partout */}
            <IconButton
              size="small"
              disableRipple
              color="inherit"
              sx={navbarIconButton}
              aria-controls="notification-menu"
              aria-haspopup="true"
              variant="contained"
              onClick={handleOpenMenu}
            >
              <Badge
                badgeContent={notificationCount}
                color="error"
                invisible={notificationCount === 0}
                sx={{
                  "& .MuiBadge-badge": {
                    fontSize: "0.65rem",
                    height: "18px",
                    minWidth: "18px",
                    padding: "0 4px",
                  }
                }}
              >
                <Icon sx={iconsStyle}>notifications</Icon>
              </Badge>
            </IconButton>

            {/* Avatar - APRÈS les notifications */}
            <Link to="/profile" style={{ textDecoration: "none" }}>
              <Avatar
                sx={{
                  bgcolor: "#1A73E8",
                  width: { xs: 40, md: 36 },
                  height: { xs: 40, md: 36 },
                  fontSize: { xs: "1rem", md: "0.875rem" },
                  fontWeight: "bold",
                  cursor: "pointer",
                  "&:hover": {
                    bgcolor: "#1557B0",
                  },
                }}
              >
                {userInitials}
              </Avatar>
            </Link>

            {/* Settings - visible seulement sur desktop */}
            <IconButton
              size="small"
              disableRipple
              color="inherit"
              sx={{ ...navbarIconButton, display: { xs: "none", lg: "inline-flex" } }}
              onClick={handleConfiguratorOpen}
            >
              <Icon sx={iconsStyle}>settings</Icon>
            </IconButton>
            {renderMenu()}
          </MDBox>
        </MDBox>
      </Toolbar>
    </AppBar>
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
  contractInfo: null,
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
  contractInfo: PropTypes.object,
};

export default DashboardNavbar;
